/**
 * Canonical sport normalization — mirrors backend/sport_normalization.py.
 * Keep alias sets in sync with shared/sport_normalization_fixtures.json.
 */

export const CANONICAL_SPORTS = ["cycling", "running", "walking", "hiking", "swimming", "other"];

export const CANONICAL_SPORT_LABELS = {
  all: "All activities",
  cycling: "Cycling",
  running: "Running",
  walking: "Walking",
  hiking: "Hiking",
  swimming: "Swimming",
  other: "Other",
};

const CYCLING_ALIASES = new Set([
  "cycling",
  "ride",
  "bike",
  "bicycle",
  "road",
  "road_cycling",
  "biking",
  "mountain_biking",
  "mountainbike",
  "mountain_bike",
  "mtb",
  "gravel",
  "virtualride",
  "virtual_ride",
  "indoor_cycling",
  "indoorcycling",
  "trainer",
  "ebikeride",
  "handcycle",
  "cyclocross",
  "cross_country_cycling",
  "emountain_bike",
  "e_bike_ride",
]);

const RUNNING_ALIASES = new Set([
  "running",
  "run",
  "virtualrun",
  "virtual_run",
  "treadmill",
  "trail_run",
  "trailrunning",
  "trail_running",
  "jogging",
  "jog",
]);

const WALKING_ALIASES = new Set(["walking", "walk"]);
const HIKING_ALIASES = new Set(["hiking", "hike"]);
const SWIMMING_ALIASES = new Set([
  "swimming",
  "swim",
  "pool_swim",
  "open_water_swim",
  "open_water",
  "pool",
]);
const OTHER_ALIASES = new Set(["other", "workout", "activity", "generic", "unknown"]);

const INDOOR_TOKENS = new Set([
  "virtualride",
  "virtual_ride",
  "virtualrun",
  "virtual_run",
  "indoor_cycling",
  "indoorcycling",
  "trainer",
  "treadmill",
  "zwift",
  "mywhoosh",
  "indoor",
  "virtual",
  "pool_swim",
  "pool",
]);

const SUBTYPE_BY_TOKEN = {
  virtualride: "virtual",
  virtual_ride: "virtual",
  virtualrun: "virtual",
  virtual_run: "virtual",
  treadmill: "treadmill",
  trail_run: "trail",
  trailrunning: "trail",
  trail_running: "trail",
  pool_swim: "pool",
  pool: "pool",
  open_water_swim: "open_water",
  open_water: "open_water",
  gravel: "gravel",
  mountain_biking: "mtb",
  mountainbike: "mtb",
  mountain_bike: "mtb",
  mtb: "mtb",
  road: "road",
  road_cycling: "road",
};

const SPORT_ALIASES = new Map();
for (const alias of CYCLING_ALIASES) SPORT_ALIASES.set(alias, "cycling");
for (const alias of RUNNING_ALIASES) SPORT_ALIASES.set(alias, "running");
for (const alias of WALKING_ALIASES) SPORT_ALIASES.set(alias, "walking");
for (const alias of HIKING_ALIASES) SPORT_ALIASES.set(alias, "hiking");
for (const alias of SWIMMING_ALIASES) SPORT_ALIASES.set(alias, "swimming");
for (const alias of OTHER_ALIASES) SPORT_ALIASES.set(alias, "other");

export function normalizeSportToken(raw) {
  if (raw == null) return "";
  let s = String(raw).trim().toLowerCase();
  if (s.includes(".")) {
    s = s.split(".").pop();
  }
  s = s.replace(/[\s\-/]+/g, "_").replace(/_+/g, "_").replace(/^_|_$/g, "");
  return s;
}

export function resolveCanonicalSport(token) {
  if (!token) return "other";
  if (CANONICAL_SPORTS.includes(token)) return token;
  return SPORT_ALIASES.get(token) || "other";
}

function detectIndoor(token, { trainer = false, isIndoor = null, filename = null } = {}) {
  if (isIndoor != null) return Boolean(isIndoor);
  if (trainer) return true;
  if (INDOOR_TOKENS.has(token)) return true;
  if (filename) {
    const fn = String(filename).toLowerCase();
    if (fn.includes("zwift") || fn.includes("mywhoosh")) return true;
  }
  return false;
}

function detectSubtype(token, subSport = null) {
  if (subSport) {
    const subToken = normalizeSportToken(subSport);
    if (SUBTYPE_BY_TOKEN[subToken]) return SUBTYPE_BY_TOKEN[subToken];
    if (["pool", "open_water", "virtual", "trail", "gravel", "mtb", "road", "treadmill"].includes(subToken)) {
      return subToken;
    }
  }
  return SUBTYPE_BY_TOKEN[token] || null;
}

export function normalizeActivitySport(rawSport, options = {}) {
  const {
    subSport = null,
    trainer = false,
    isIndoor = null,
    filename = null,
  } = options;
  const token = normalizeSportToken(rawSport);
  const sport = resolveCanonicalSport(token);
  const sportSubtype = detectSubtype(token, subSport);
  const is_indoor = detectIndoor(token, { trainer, isIndoor, filename });
  return {
    sport,
    sport_subtype: sportSubtype,
    is_indoor,
    raw_sport: rawSport != null ? String(rawSport) : null,
  };
}

export function normalizeActivitySportFields(activity = {}) {
  const raw = activity.raw_sport || activity.sport;
  const normalized = normalizeActivitySport(raw, {
    subSport: activity.sport_subtype || activity.sub_sport,
    trainer: Boolean(activity.trainer),
    isIndoor: activity.is_indoor,
    filename: activity.filename,
  });
  return {
    sport: normalized.sport,
    sport_subtype: normalized.sport_subtype,
    is_indoor: normalized.is_indoor,
    raw_sport: normalized.raw_sport || (raw != null ? String(raw) : null),
  };
}

export function canonicalSportForFilter(activityOrSport) {
  if (activityOrSport && typeof activityOrSport === "object") {
    const sport = activityOrSport.sport;
    if (CANONICAL_SPORTS.includes(sport)) return sport;
    return normalizeActivitySport(
      activityOrSport.raw_sport || sport,
      {
        subSport: activityOrSport.sport_subtype || activityOrSport.sub_sport,
        trainer: Boolean(activityOrSport.trainer),
        isIndoor: activityOrSport.is_indoor,
        filename: activityOrSport.filename,
      }
    ).sport;
  }
  return normalizeActivitySport(activityOrSport).sport;
}

export function isIndoorActivity(activity = {}) {
  if (activity.is_indoor != null) return Boolean(activity.is_indoor);
  const token = normalizeSportToken(activity.raw_sport || activity.sport);
  return detectIndoor(token, {
    trainer: Boolean(activity.trainer),
    isIndoor: activity.is_indoor,
    filename: activity.filename,
  });
}

export function sportFilterLabel(sportKey) {
  return CANONICAL_SPORT_LABELS[sportKey] || String(sportKey || "Other");
}

export function sportDisplayLabel(activity = {}) {
  const canonical = canonicalSportForFilter(activity);
  const base = sportFilterLabel(canonical);
  if (isIndoorActivity(activity)) {
    if (canonical === "cycling") return "Indoor Cycling";
    if (canonical === "running") return "Indoor Running";
    if (canonical === "swimming") return activity.sport_subtype === "pool" ? "Pool Swim" : base;
    return `Indoor ${base}`;
  }
  if (activity.sport_subtype === "open_water" && canonical === "swimming") return "Open Water Swim";
  return base;
}

export function filterActivitiesByCanonicalSport(activities, sportFilter) {
  if (!sportFilter || sportFilter === "all") return activities;
  if (sportFilter === "other") {
    return activities.filter((a) => canonicalSportForFilter(a) === "other");
  }
  return activities.filter((a) => canonicalSportForFilter(a) === sportFilter);
}

export function availableCanonicalSportFilters(activities) {
  const present = new Set(activities.map((a) => canonicalSportForFilter(a)));
  return ["all", ...CANONICAL_SPORTS.filter((s) => present.has(s))];
}
